//#region IMPORTS
import { MAX_SPREADSHEET_SHEET_NAME_LENGTH } from "../models/_consts";
import { InstoreOrder } from "../models/instoreOrder";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import agent from "../api/agent";
//#endregion

export const useExportInstoreOrderPersoItemsExcel = () => {
  // Function to auto-fit columns based on data
  const autoFitColumns = (worksheet: XLSX.WorkSheet, jsonData: any[]) => {
    const objectMaxLength: number[] = [];

    jsonData.forEach((row) => {
      Object.values(row).forEach((val: unknown, i) => {
        const strVal = val !== null && val !== undefined ? String(val) : "";
        const length = strVal.length || 10; // default length if empty
        objectMaxLength[i] = Math.max(objectMaxLength[i] || 10, length);
      });
    });

    (worksheet as any)["!cols"] = objectMaxLength.map((width) => ({
      wch: width + 2,
    }));
  };

  const exportInstoreOrderPersoItems = async (
    instoreOrderId: number,
    merged: boolean = false
  ) => {
    await toast.promise(
      (async () => {
        const response = await agent.InstoreOrders.detail(instoreOrderId);

        if (!response?.data) {
          throw new Error("In-store order not found");
        }

        const instoreOrder: InstoreOrder = response.data;
        const workbook = XLSX.utils.book_new();

        const linesWithCustomFields = instoreOrder.instore_order_lines.filter(
          (line) =>
            Array.isArray(line.custom_fields) && line.custom_fields.length > 0
        );

        if (linesWithCustomFields.length === 0) {
          throw new Error("No personalized items to export");
        }

        // MERGED MODE FOR EASY CATALOG
        if (merged) {
          const mergedData = linesWithCustomFields.flatMap((line) =>
            line.custom_fields.map((field, i) => ({
              EC_ID: `${line.article_name}_${i}`,
              persoItem: line.article_name ?? "article",
              fieldName: field.fieldName,
              fieldValue: field.fieldValue,
            }))
          );

          const worksheet = XLSX.utils.json_to_sheet(mergedData);
          autoFitColumns(worksheet, mergedData);
          XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Personalized Items"
          );
        }
        // PER-SHEET MODE
        else {
          linesWithCustomFields.forEach((line) => {
            const sheetData = line.custom_fields.map((field, i) => ({
              EC_ID: `${line.article_name}_${i}`,
              fieldName: field.fieldName,
              fieldValue: field.fieldValue,
            }));

            const worksheet = XLSX.utils.json_to_sheet(sheetData);
            autoFitColumns(worksheet, sheetData);
            const rawSheetName = line.article_name || "article";
            const safeSheetName = rawSheetName.slice(
              0,
              MAX_SPREADSHEET_SHEET_NAME_LENGTH
            );

            XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);
          });
        }

        XLSX.writeFile(
          workbook,
          `instore-order-${instoreOrder.order_code}-perso-items${
            merged ? "-merged" : ""
          }.xlsx`
        );
      })(),
      {
        pending: "Preparing spreadsheet export.",
        success: "Spreadsheet exported successfully.",
        error: {
          render({ data }) {
            return data instanceof Error
              ? data.message
              : "Unexpected error while exporting Excel";
          },
        },
      }
    );
  };

  return {
    exportInstoreOrderPersoItems,
  };
};
